import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";

export const ROLE_OPTS = [["SUPERADMIN", "Superadmin"], ["DIRECAO", "Direção"], ["COORDENACAO", "Coordenação"], ["PROFESSOR", "Professor"], ["COZINHEIRA", "Cozinheira"]];
const EMPTY = { title: "", description: "", menu: "", routes: "", roles: [], steps: "", faq: "", highlights: "", published: true };

const toForm = (a) => a ? {
  title: a.title, description: a.description || "", menu: a.menu || "", routes: (a.routes || []).join(", "), roles: a.roles || [],
  steps: (a.steps || []).join("\n"), faq: (a.faq || []).map((f) => `${f.q} | ${f.a}`).join("\n"),
  highlights: (a.highlights || []).map((h) => `${h.testid} | ${h.label}`).join("\n"), published: a.published !== false,
} : EMPTY;

const lines = (t) => t.split("\n").map((l) => l.trim()).filter(Boolean);
const pairs = (t) => lines(t).map((l) => l.split("|").map((x) => x.trim())).filter((p) => p.length >= 2 && p[0] && p[1]);

const toPayload = (f) => ({
  title: f.title.trim(), description: f.description.trim(), menu: f.menu.trim(),
  routes: f.routes.split(",").map((r) => r.trim()).filter(Boolean), roles: f.roles, steps: lines(f.steps),
  faq: pairs(f.faq).map(([q, ...a]) => ({ q, a: a.join(" | ") })),
  highlights: pairs(f.highlights).map(([testid, ...l]) => ({ testid, label: l.join(" ") })), published: f.published,
});

export function ArticleDialog({ article, open, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(toForm(article)); }, [open, article]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const toggleRole = (r) => setF((p) => ({ ...p, roles: p.roles.includes(r) ? p.roles.filter((x) => x !== r) : [...p.roles, r] }));

  const save = async () => {
    if (!f.title.trim() || !f.roles.length) { toast.error("Informe o nome e ao menos um perfil"); return; }
    setSaving(true);
    try {
      if (article) await api.put(`/assistant/admin/kb/${article.id}`, toPayload(f));
      else await api.post("/assistant/admin/kb", toPayload(f));
      toast.success("Tutorial salvo");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="kb-article-dialog">
        <DialogHeader><DialogTitle>{article ? "Editar tutorial" : "Novo tutorial"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nome da funcionalidade *</Label><Input value={f.title} onChange={set("title")} data-testid="kb-title-input" /></div>
          <div><Label>Para que serve</Label><Textarea rows={2} value={f.description} onChange={set("description")} data-testid="kb-description-input" /></div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>Localização no menu</Label><Input value={f.menu} onChange={set("menu")} placeholder="Menu → Estudantes" data-testid="kb-menu-input" /></div>
            <div><Label>Rotas da tela (separadas por vírgula)</Label><Input value={f.routes} onChange={set("routes")} placeholder="/alunos, /alunos/:id" data-testid="kb-routes-input" /></div>
          </div>
          <div>
            <Label>Perfis autorizados *</Label>
            <div className="mt-1.5 flex flex-wrap gap-3">
              {ROLE_OPTS.map(([v, l]) => (
                <label key={v} className="flex items-center gap-1.5 text-sm"><Checkbox checked={f.roles.includes(v)} onCheckedChange={() => toggleRole(v)} data-testid={`kb-role-${v}`} />{l}</label>
              ))}
            </div>
          </div>
          <div><Label>Passo a passo (um passo por linha)</Label><Textarea rows={7} value={f.steps} onChange={set("steps")} data-testid="kb-steps-input" /></div>
          <div><Label>Dúvidas frequentes (uma por linha: pergunta | resposta)</Label><Textarea rows={3} value={f.faq} onChange={set("faq")} data-testid="kb-faq-input" /></div>
          <div><Label>Elementos para destacar na tela (um por linha: data-testid | nome do botão)</Label><Textarea rows={2} value={f.highlights} onChange={set("highlights")} data-testid="kb-highlights-input" /></div>
          <label className="flex items-center gap-2 text-sm font-medium"><Switch checked={f.published} onCheckedChange={(v) => setF((p) => ({ ...p, published: v }))} data-testid="kb-published-switch" />Publicado (o assistente usa este tutorial)</label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="kb-cancel-btn">Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="kb-save-btn">{saving ? "Salvando..." : "Salvar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
